import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { UserCheck, UploadCloud } from "lucide-react";

const STATUS_STYLES = {
  none:     "bg-panel text-dim border-line",
  pending:  "bg-orange-500/10 text-orange-400 border-orange-500/40",
  approved: "bg-buy-dim text-buy border-buy",
  rejected: "bg-sell-dim text-sell border-sell",
};

export default function KycPanel() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ document_type: "id_card", full_name: "", date_of_birth: "", country: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/kyc/me"); setInfo(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please attach a document");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("document_type", form.document_type);
      fd.append("full_name", form.full_name);
      fd.append("date_of_birth", form.date_of_birth);
      fd.append("country", form.country);
      fd.append("document", file);
      await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("KYC submitted");
      setFile(null); setForm({ document_type: "id_card", full_name: "", date_of_birth: "", country: "" });
      await refreshUser(); load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const status = info?.status || "none";
  const badgeClass = STATUS_STYLES[status] || STATUS_STYLES.none;
  const canSubmit = status === "none" || status === "rejected";

  return (
    <div className="bg-panel border border-line rounded-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-buy" />
          <h2 className="font-semibold text-sm uppercase tracking-widest">{t("kyc")}</h2>
        </div>
        <span data-testid="kyc-status" className={`text-[10px] uppercase tracking-widest px-2 py-1 border rounded-sm ${badgeClass}`}>
          {t("kyc_" + status)}
        </span>
      </div>
      {status === "rejected" && info?.rejection_reason && (
        <div className="mb-3 text-xs text-sell font-mono">Reason: {info.rejection_reason}</div>
      )}
      {status === "approved" && (
        <div className="text-sm text-buy">Your identity has been verified.</div>
      )}
      {status === "pending" && (
        <div className="text-sm text-muted">Your submission is under review.</div>
      )}
      {canSubmit && (
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("kyc_doc_type")}</label>
              <select data-testid="kyc-doc-type" value={form.document_type} onChange={(e) => setForm({...form, document_type: e.target.value})}
                className="w-full mt-1 bg-app border border-line px-3 py-2 rounded-sm outline-none">
                <option value="id_card">{t("id_card")}</option>
                <option value="passport">{t("passport")}</option>
                <option value="drivers_license">{t("drivers_license")}</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("kyc_country")}</label>
              <input data-testid="kyc-country" required value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}
                className="w-full mt-1 bg-app border border-line px-3 py-2 rounded-sm outline-none" placeholder="Türkiye" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("kyc_full_name")}</label>
              <input data-testid="kyc-full-name" required value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}
                className="w-full mt-1 bg-app border border-line px-3 py-2 rounded-sm outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("kyc_dob")}</label>
              <input data-testid="kyc-dob" required type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})}
                className="w-full mt-1 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-dim">{t("kyc_upload")}</label>
            <label className="mt-1 flex items-center justify-center gap-2 bg-app border border-dashed border-line px-3 py-6 rounded-sm cursor-pointer hover:bg-panel-hover text-muted text-sm">
              <UploadCloud className="w-5 h-5" />
              {file ? file.name : "Click to select image (max 5MB)"}
              <input data-testid="kyc-file" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <button data-testid="kyc-submit" disabled={busy} type="submit"
            className="w-full bg-buy text-black font-bold py-2.5 rounded-sm hover:bg-emerald-400 uppercase text-xs tracking-widest disabled:opacity-50">
            {t("submit")}
          </button>
        </form>
      )}
    </div>
  );
}
